/**
 * Playground examples — ordered so each introduces exactly one new
 * layer or feature as the user scrolls down the list.
 *
 *   Tier 1  (1–3)   basic syntax: pipeline shape, multi-actor, shared actions
 *   Tier 2  (4–6)   structural overlays: FORK, JOIN, CYCLE
 *   Tier 3  (7–8)   auto-dispatched sub-topologies: STAR→radial, TREE→mrtree
 *   Tier 4  (9)     `at:` pools & cross-pool edges
 *   Tier 5  (10–11) failure modes: DEADLOCK, OVERFLOW
 *   Tier 6  (12–13) composites: SUBSYSTEMS, grand-combo with auto-dispatch
 *
 * Every structural overlay (PIPELINE / FORK / JOIN / CYCLE / HUB /
 * DEADLOCK / OVERFLOW / SUBSYSTEMS) and every sub-topology dispatch
 * (PATH→layered, STAR→radial, TREE→mrtree, CYCLIC→layered) appears
 * somewhere in this list.
 *
 * Formatting: each statement spans 4–5 lines — subject, do:, needs:,
 * yields:, at: — one field per line. The parser accepts either inline
 * or multi-line form (whitespace and newlines are interchangeable
 * between fields), and the multi-line layout reads top-to-bottom like
 * a recipe.
 */

export interface Example {
    name: string
    description: string
    source: string
}

export const examples: Example[] = [

// ─── Tier 1: syntax basics ───

{
    name: "Pipeline",
    description: "Three steps in a row. Each step's output feeds the next. Overlay: PIPELINE.",
    source: `# Linear chain — output of each step is the input of the next.

barista
do: grind beans
needs: coffee beans
yields: ground beans
at: cafe

barista
do: brew coffee
needs: ground beans
yields: brewed coffee
at: cafe

barista
do: serve customer
needs: brewed coffee
yields: served coffee
at: cafe`,
},

{
    name: "Hand-off pipeline",
    description: "Four actors, four stages — each owns one step. Demonstrates per-actor coloring across four pools.",
    source: `# Different actor for each stage — role hand-off between locations.

writer
do: draft article
needs: brief
yields: article draft
at: writers room

editor
do: edit article
needs: article draft
yields: edited article
at: editorial

designer
do: design layout
needs: edited article
yields: page layout
at: design studio

publisher
do: publish issue
needs: page layout
yields: published issue
at: press`,
},

{
    name: "Shared action",
    description: "Two roles perform one action together. Comma-separated subjects share a single action node.",
    source: `# Engineering and security review the spec jointly — ONE action node,
# TWO performers, NOT two parallel actions.

engineer, security
do: review design
needs: spec
yields: approved spec
at: meeting room

engineer
do: implement
needs: approved spec
yields: code
at: engineering

security
do: audit code
needs: code
yields: audit report
at: security`,
},

// ─── Tier 2: structural overlays (auto-detected) ───

{
    name: "Fork",
    description: "One source feeds three downstream chains. Overlay: FORK (each branch becomes its own PIPELINE).",
    source: `# A single dough divides into three baked goods.

mixer
do: mix dough
needs: flour
yields: dough
at: prep

baker
do: bake bread
needs: dough
yields: bread
at: ovens

baker
do: cool bread
needs: bread
yields: cooled bread
at: racks

baker
do: bake croissants
needs: dough
yields: croissants
at: ovens

baker
do: cool croissants
needs: croissants
yields: cooled croissants
at: racks

baker
do: bake rolls
needs: dough
yields: rolls
at: ovens

baker
do: cool rolls
needs: rolls
yields: cooled rolls
at: racks`,
},

{
    name: "Join",
    description: "Three independent prep streams merge into one final product. Overlay: JOIN.",
    source: `# Three prep streams converge in the final blend.

chef
do: blend fruit
needs: bananas, berries
yields: fruit puree
at: kitchen

chef
do: blend dairy
needs: yoghurt, milk
yields: dairy mix
at: kitchen

chef
do: blend ice base
needs: ice cubes, water
yields: chilled base
at: kitchen

chef
do: combine smoothie
needs: fruit puree, dairy mix, chilled base
yields: smoothie
at: kitchen`,
},

{
    name: "Feedback loop",
    description: "A control loop where the output feeds back as input. Overlay: CYCLE (cycle-broken for layered display).",
    source: `# Thermostat reads temperature, compares to target, asks the heater
# to adjust the room air — which is what the thermostat reads next.

thermostat
do: read temperature
needs: room air
yields: current temperature
at: hvac

thermostat
do: compare
needs: current temperature, target temperature
yields: control signal
at: hvac

heater
do: adjust output
needs: control signal
yields: room air
at: hvac`,
},

// ─── Tier 3: sub-topology dispatch (auto-selected layouts) ───

{
    name: "Star topology",
    description: "One dispatcher fans out to four parallel workers. The pool's action subgraph is a STAR, so the renderer auto-dispatches RADIAL layout inside the pool.",
    source: `# A notification service dispatches one event to four channels —
# email, SMS, push, webhook. The hub is 'dispatch alert', the spokes
# are the four workers. Classifier: STAR → radial layout.

notification service
do: dispatch alert
needs: event trigger
yields: email message, sms message, push message, webhook message
at: alerts

email worker
do: send email
needs: email message
yields: email sent
at: alerts

sms worker
do: send sms
needs: sms message
yields: sms delivered
at: alerts

mobile worker
do: push notification
needs: push message
yields: push delivered
at: alerts

api worker
do: fire webhook
needs: webhook message
yields: webhook fired
at: alerts`,
},

{
    name: "Tree topology",
    description: "A parent task branches into children that branch again. The pool's action subgraph is a TREE, so the renderer auto-dispatches MRTREE layout inside the pool.",
    source: `# Product work breakdown — scope fans into design / engineering / launch,
# each then fans into a verification step. A two-level rooted tree.
# Classifier: TREE → mrtree layout.

project lead
do: scope project
needs: product brief
yields: design task, engineering task, launch task
at: plan

designer
do: create mockups
needs: design task
yields: mockups
at: plan

engineer
do: build product
needs: engineering task
yields: built product
at: plan

marketer
do: prepare launch
needs: launch task
yields: launch plan
at: plan

qa
do: review mockups
needs: mockups
yields: design approved
at: plan

qa
do: test product
needs: built product
yields: product verified
at: plan

coo
do: sign off launch
needs: launch plan
yields: launch approved
at: plan`,
},

// ─── Tier 4: pools & cross-pool flow ───

{
    name: "Multi-location order",
    description: "Order moves through four services, each in its own location pool. Shows cross-pool edges with boundary labels.",
    source: `# Four services, four boundaries. Notice how location pools render
# as separate panels with cross-boundary edges.

customer
do: place order
needs: cart
yields: order
at: web

bank
do: charge card
needs: order, payment method
yields: payment receipt
at: payments

warehouse
do: pick items
needs: order, payment receipt
yields: package
at: warehouse

courier
do: deliver
needs: package, address
yields: delivered shipment
at: shipping`,
},

// ─── Tier 5: failure modes ───

{
    name: "Deadlock risk",
    description: "Two workers each waiting on the other's signal — circular wait. Overlay: DEADLOCK.",
    source: `# Each worker can only emit its signal AFTER finishing its wait —
# but the wait depends on the other's signal. Mutual deadlock.
# The math engine proves this via uncovered siphons (see \`live\`).

worker a
do: acquire lock
needs: lock
yields: lock held a
at: process a

worker a
do: wait for b
needs: lock held a, signal from b
yields: done a, lock, signal from a
at: process a

worker b
do: acquire lock
needs: lock
yields: lock held b
at: process b

worker b
do: wait for a
needs: lock held b, signal from a
yields: done b, lock, signal from b
at: process b`,
},

{
    name: "Resource leak",
    description: "Self-replicating culture leaks a byproduct that has no consumer. Overlay: OVERFLOW.",
    source: `# Yeast cells are consumed AND produced by the same action — a
# self-loop. Alcohol is yielded but nothing consumes it, so it
# accumulates without bound. Math: an unbounded T-invariant.

yeast culture
do: ferment
needs: yeast cells
yields: yeast cells, alcohol
at: fermenter`,
},

// ─── Tier 6: composites ───

{
    name: "Three teams",
    description: "Three independent teams, no shared work — each cluster has a DIFFERENT internal shape. Engineering is a PIPELINE, support is a CYCLE (feedback loop), marketing is a FORK (one brief fans out to three channels). One diagram exercising three distinct sub-topologies. Overlays: SUBSYSTEMS, PIPELINES, CYCLES, FORKS.",
    source: `# Three disconnected teams, three different topologies. Toggle
# the overlays to see each shape light up independently.

# Engineering — PIPELINE (linear flow)

engineer
do: write code
needs: requirements
yields: code
at: engineering

engineer
do: deploy app
needs: code
yields: deployed app
at: engineering

# Support — CYCLE (ticket queue feeds back to triage)

support agent
do: triage
needs: ticket queue, customer message
yields: open ticket
at: support

support agent
do: respond
needs: open ticket
yields: ticket queue
at: support

# Marketing — FORK (one brief fans out to three channels)

marketer
do: write brief
needs: campaign idea
yields: campaign brief
at: marketing

ads team
do: design banners
needs: campaign brief
yields: banner ads
at: marketing

social team
do: write posts
needs: campaign brief
yields: social posts
at: marketing

email team
do: draft blast
needs: campaign brief
yields: email blast
at: marketing`,
},

{
    name: "Online retailer",
    description: "Grand combination: six location pools — browse (PIPELINE), checkout (JOIN), payments (STAR→radial auto-dispatch), fulfillment (TREE→mrtree auto-dispatch), notifications (STAR→radial auto-dispatch), support (CYCLE + DEADLOCK). Every overlay AND every auto-dispatched sub-topology in one diagram.",
    source: `# A complete e-commerce flow demonstrating every layer AND every
# sub-topology dispatch in one diagram. As you toggle layers you'll
# see PIPELINE / JOIN / FORK / CYCLE / HUB / DEADLOCK overlays, and
# the renderer will automatically use RADIAL for payments and
# notifications (STAR shape) and MRTREE for fulfillment (TREE shape).

# ── browse: PIPELINE (PATH → layered LR) ──

shopper
do: search products
needs: query
yields: search results
at: browse

shopper
do: view product
needs: search results
yields: product page
at: browse

# ── checkout: JOIN (two inputs merge) ──

cart service
do: validate cart
needs: product page, account info
yields: validated cart
at: checkout

cart service
do: apply coupons
needs: validated cart, coupon code
yields: priced cart
at: checkout

# ── payments: STAR (one charge → four outcomes → radial dispatch) ──

gateway
do: process payment
needs: priced cart, payment method
yields: success signal, decline signal, fraud signal, retry signal
at: payments

gateway
do: confirm success
needs: success signal
yields: payment success
at: payments

gateway
do: handle decline
needs: decline signal
yields: payment declined
at: payments

fraud service
do: review fraud
needs: fraud signal
yields: fraud flagged
at: payments

gateway
do: enqueue retry
needs: retry signal
yields: retry queued
at: payments

# ── fulfillment: TREE (pick → branch to 3 prep steps → ship → mrtree) ──

warehouse
do: pick items
needs: payment success
yields: packing task, labelling task, weighing task
at: fulfillment

warehouse
do: pack box
needs: packing task
yields: packed box
at: fulfillment

warehouse
do: print label
needs: labelling task
yields: shipping label
at: fulfillment

warehouse
do: weigh package
needs: weighing task
yields: weight record
at: fulfillment

courier
do: ship
needs: packed box, shipping label, weight record, address
yields: shipped order
at: fulfillment

# ── notifications: STAR (one event → four channels → radial dispatch) ──

notification service
do: dispatch alert
needs: shipped order
yields: email event, sms event, push event, webhook event
at: notifications

email worker
do: send email
needs: email event
yields: email sent
at: notifications

sms worker
do: send sms
needs: sms event
yields: sms delivered
at: notifications

mobile worker
do: push notification
needs: push event
yields: push delivered
at: notifications

api worker
do: fire webhook
needs: webhook event
yields: webhook fired
at: notifications

# ── support: CYCLE + DEADLOCK (closed loop) ──

support agent
do: read inquiry
needs: customer message, prior reply
yields: inquiry log
at: support

support agent
do: send reply
needs: inquiry log
yields: prior reply
at: support`,
},

]
